import { useEffect, useState } from "react";
import { fileUrl } from "@/lib/api";

const IMG_TYPES = new Set(["image", "furniture"]);

export function useAspects(urls) {
  const [map, setMap] = useState({});
  useEffect(() => {
    urls.forEach((u) => {
      if (!u || map[u] !== undefined) return;
      const img = new Image();
      img.onload = () => setMap((m) => ({ ...m, [u]: img.naturalWidth / Math.max(1, img.naturalHeight) }));
      img.onerror = () => setMap((m) => ({ ...m, [u]: 1.33 }));
      img.src = fileUrl(u);
    });
  }, [urls.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps
  return map;
}

export function roomContent(room, els) {
  const images = els.filter((e) => IMG_TYPES.has(e.type) && e.data?.url).sort((a, b) => b.w * b.h - a.w * a.h);
  const colours = [...els.filter((e) => e.type === "palette").flatMap((e) => e.data?.colors || []), ...els.filter((e) => e.type === "swatch").map((e) => ({ hex: e.data?.hex, name: e.data?.name, code: [e.data?.brand, e.data?.code].filter(Boolean).join(" ") }))].filter((c) => c?.hex).filter((c, i, arr) => arr.findIndex((x) => x.hex?.toLowerCase() === c.hex?.toLowerCase()) === i).slice(0, 8);
  const materials = els.filter((e) => e.type === "material");
  const heading = els.find((e) => e.type === "heading")?.data || {};
  const bullets = els.filter((e) => e.type === "note" && e.data?.text).flatMap((e) => e.data.text.split("\n")).map((t) => t.replace(/^[-•*]\s*/, "").trim()).filter(Boolean).slice(0, 7);
  const dims = els.filter((e) => e.type === "dimension" && e.data?.text).map((e) => e.data.text);
  const intro = room.story || (room.narration ? room.narration.split(/(?<=[.!?])\s/).slice(0, 2).join(" ") : "");
  return { images, colours, materials, heading, bullets, dims, intro, title: heading.title || room.name, subtitle: heading.subtitle || "" };
}

export function pagesFor(room, els) {
  const c = roomContent(room, els);
  const first = c.images.slice(0, 4);
  const rest = c.images.slice(4);
  const pages = [{ kind: "spread", room, c, images: first }];
  for (let i = 0; i < rest.length; i += 6) pages.push({ kind: "gallery", room, c, images: rest.slice(i, i + 6), n: i / 6 + 2 });
  return pages;
}

function Img({ el, className = "" }) {
  const [broken, setBroken] = useState(false);
  if (broken) return null;
  return (
    <figure className={`relative overflow-hidden rounded-xl bg-stone-200/40 ${className}`}>
      <img src={fileUrl(el.data.url)} alt={el.data.caption || ""} onError={() => setBroken(true)} className="h-full w-full object-cover" />
      {el.data.caption && <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-3 pb-2 pt-6 text-[10px] uppercase tracking-[0.2em] text-white/90">{el.data.caption}</figcaption>}
    </figure>
  );
}

function TextPanel({ c, dark, className = "", max = 7 }) {
  return (
    <div className={`${dark ? "bg-stone-900/75 text-white backdrop-blur-sm" : "bg-white/85 text-stone-900 backdrop-blur-sm"} rounded-xl p-5 sm:p-7 ${className}`} data-testid="spread-text">
      {c.subtitle && <p className={`text-[10px] uppercase tracking-[0.3em] ${dark ? "text-white/60" : "text-stone-500"}`}>{c.subtitle}</p>}
      <h2 className="font-serif text-3xl leading-tight sm:text-4xl">{c.title}</h2>
      {c.intro && <p className={`mt-3 text-sm leading-relaxed sm:text-[15px] ${dark ? "text-white/85" : "text-stone-700"}`}>{c.intro}</p>}
      {c.bullets.length > 0 && <ul className={`mt-4 space-y-1.5 border-t pt-4 text-sm ${dark ? "border-white/20 text-white/90" : "border-stone-300 text-stone-800"}`}>{c.bullets.slice(0, max).map((b, i) => <li key={`${i}-${b.slice(0, 20)}`} className="flex gap-2"><span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-current" />{b}</li>)}</ul>}
      {c.dims.length > 0 && <p className={`mt-4 font-mono text-xs ${dark ? "text-white/60" : "text-stone-500"}`}>{c.dims.join(" · ")}</p>}
    </div>
  );
}

function Colours({ colours, dark }) {
  if (!colours.length) return null;
  return (
    <div className={`rounded-xl p-4 sm:p-5 ${dark ? "bg-white/10" : "bg-white/85"}`} data-testid="spread-colours">
      <p className={`text-[10px] uppercase tracking-[0.3em] ${dark ? "text-white/60" : "text-stone-500"}`}>Colour</p>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2">{colours.map((c, i) => <div key={`${c.hex}-${i}`} className="w-[72px] text-center"><span className="mx-auto block h-9 w-9 rounded-full border border-black/10 shadow-sm sm:h-10 sm:w-10" style={{ background: c.hex }} /><p className="mt-1 truncate text-[9px] font-semibold uppercase tracking-wider">{c.name}</p>{c.code && <p className="truncate text-[8px] text-stone-500">{c.code}</p>}</div>)}</div>
    </div>
  );
}

function Materials({ materials, dark }) {
  if (!materials.length) return null;
  return (
    <div className={`rounded-xl p-4 sm:p-5 ${dark ? "bg-white/10" : "bg-white/85"}`} data-testid="spread-materials">
      <p className={`text-[10px] uppercase tracking-[0.3em] ${dark ? "text-white/60" : "text-stone-500"}`}>Materials & finishes</p>
      <div className="mt-3 flex flex-wrap gap-3">{materials.slice(0, 6).map((m) => <div key={m.id} className="w-[76px]"><div className={`h-10 w-full overflow-hidden rounded-lg border border-black/10 ${m.data?.texture || ""}`} style={m.data?.hex ? { background: m.data.hex } : {}}>{m.data?.url && <img src={fileUrl(m.data.url)} alt="" className="h-full w-full object-cover" />}</div><p className="mt-1.5 truncate text-[10px] font-semibold">{m.data?.name}</p><p className="truncate text-[9px] text-stone-500">{[m.data?.supplier, m.data?.finish].filter(Boolean).join(" · ")}</p></div>)}</div>
    </div>
  );
}

export function Spread({ page, aspects }) {
  const { c, images } = page;
  const [hero, ...support] = images;
  const landscape = !hero || (aspects[hero.data.url] ?? 1.33) >= 1.1;
  const hasBottom = c.colours.length > 0 || c.materials.length > 0;
  if (!hero) {
    return (
      <div className="grid h-full w-full gap-4 p-4 sm:grid-cols-[1.2fr_1fr] sm:p-8" data-testid="spread-no-image">
        <TextPanel c={c} />
        <div className="grid content-start gap-4"><Colours colours={c.colours} /><Materials materials={c.materials} /></div>
      </div>
    );
  }
  if (landscape) {
    return (
      <div className={`grid h-full w-full gap-3 overflow-y-auto p-3 sm:gap-4 sm:overflow-hidden sm:p-6 ${hasBottom ? "grid-rows-[minmax(0,1fr)_auto]" : "grid-rows-[minmax(0,1fr)]"}`} data-testid="spread-landscape">
        <div className={`grid min-h-0 gap-3 sm:gap-4 ${support.length ? "sm:grid-cols-[2fr_1fr]" : ""}`}>
          <div className="relative min-h-[240px]"><Img el={hero} className="absolute inset-0" /><div className="absolute left-0 top-0 max-w-[360px] p-3 sm:p-5"><TextPanel c={c} dark /></div></div>
          {support.length > 0 && <div className="grid min-h-0 gap-3 sm:gap-4" style={{ gridTemplateRows: `repeat(${Math.min(support.length, 3)}, minmax(0, 1fr))` }}>{support.slice(0, 3).map((s) => <Img key={s.id} el={s} className="min-h-[90px]" />)}</div>}
        </div>
        {hasBottom && <div className="grid gap-3 sm:grid-cols-2 sm:gap-4"><Colours colours={c.colours} /><Materials materials={c.materials} /></div>}
      </div>
    );
  }
  return (
    <div className="grid h-full w-full gap-3 overflow-y-auto p-3 sm:grid-cols-[5fr_7fr] sm:gap-4 sm:overflow-hidden sm:p-6" data-testid="spread-portrait">
      <div className="relative min-h-[260px]"><Img el={hero} className="absolute inset-0" /></div>
      <div className="grid min-h-0 gap-3 overflow-y-auto sm:gap-4" style={{ gridTemplateRows: `auto ${support.length ? "minmax(120px,1fr)" : ""} ${hasBottom ? "auto" : ""}`.trim() }}>
        <TextPanel c={c} max={5} />
        {support.length > 0 && <div className={`grid min-h-0 gap-3 sm:gap-4 ${support.length === 1 ? "" : "grid-cols-2"}`}>{support.slice(0, 2).map((s) => <Img key={s.id} el={s} className="min-h-[90px]" />)}</div>}
        {hasBottom && <div className="grid gap-3 sm:grid-cols-2"><Colours colours={c.colours} /><Materials materials={c.materials} /></div>}
      </div>
    </div>
  );
}

export function Gallery({ page }) {
  return (
    <div className="flex h-full w-full flex-col gap-3 p-3 sm:p-6" data-testid="spread-gallery">
      <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500">{page.c.title} · continued</p>
      <div className={`grid min-h-0 flex-1 gap-3 ${page.images.length <= 2 ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-3"}`}>{page.images.map((s) => <Img key={s.id} el={s} className="min-h-[120px]" />)}</div>
    </div>
  );
}

export function Cover({ title, subtitle, brandName, heroUrl, heroFocus, intro, prefs }) {
  const likes = (prefs || []).filter((p) => p.sentiment === "like" && p.image_url);
  const featured = likes.filter((p) => p.featured);
  const shown = (featured.length ? featured : likes).slice(0, 6);
  const dislikes = (prefs || []).filter((p) => p.sentiment === "dislike").slice(0, 8);
  const told = shown.length > 0 || dislikes.length > 0;
  return (
    <div className="relative h-full w-full overflow-hidden" data-testid="presentation-cover-slide">
      {heroUrl && <img src={fileUrl(heroUrl)} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: `${heroFocus?.x ?? 50}% ${heroFocus?.y ?? 50}%` }} />}
      <div className={`absolute inset-0 ${heroUrl ? "bg-gradient-to-r from-stone-950/85 via-stone-950/40 to-stone-950/20" : "bg-stone-900"}`} />
      <div className="relative flex h-full flex-col justify-between p-6 text-white sm:p-12">
        <div className="max-w-xl">
          <p className="text-[10px] uppercase tracking-[0.35em] text-white/60">{brandName} · Design proposal</p>
          <h1 className="mt-3 font-serif text-4xl leading-[1.02] sm:text-6xl">{title}</h1>
          {subtitle && <p className="mt-3 text-sm text-white/70">{subtitle}</p>}
          {intro && <p className="mt-6 max-w-md text-[15px] leading-relaxed text-white/85">{intro}</p>}
        </div>
        {told && (
          <div className="mt-8 max-w-3xl rounded-xl bg-stone-900/70 p-5 backdrop-blur-sm sm:p-6" data-testid="what-you-told-us">
            <p className="text-[10px] uppercase tracking-[0.35em] text-white/60">What you told us</p>
            {shown.length > 0 && <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-6">{shown.map((p) => <figure key={p.id} data-testid={`told-like-${p.id}`}><img src={fileUrl(p.image_url)} alt={p.title} className="aspect-square w-full rounded-lg object-cover" /><figcaption className="mt-1.5 truncate text-[11px] text-white/85" title={p.note || p.title}>{p.note ? `“${p.note}”` : p.title}</figcaption></figure>)}</div>}
            {dislikes.length > 0 && <p className="mt-4 flex flex-wrap gap-x-3 gap-y-1 text-[11px] uppercase tracking-wider text-white/50" data-testid="told-dislikes">{dislikes.map((p) => <span key={p.id}>no {(p.title || p.note || p.kind).toLowerCase()}</span>)}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
